import React, { useMemo, useState } from 'react';
import { Card, Space, Typography, Button } from 'antd';
import { FileTextOutlined, UploadOutlined } from '@ant-design/icons';
import type { RouteViewModelDocuments, RouteViewModelDocumentRequirement } from '../../../types/routeViewModel';
import { documentTypeLabel } from '../../../utils/enumUiLabels';
import type { Route } from '../../../types';
import UploadDocumentModal from '../../documents/UploadDocumentModal';
import {
  isBusinessDocumentDefinitionActive,
  listBusinessDocumentDefinitions,
  normalizeBusinessTransportMode,
  type BusinessDocumentDefinition,
} from '../../../utils/businessDocumentDefinitions';
import {
  routeDocMatchesRequirement,
  uploadPresetForRouteRequirement,
} from './routeDocumentsShared';

const { Text } = Typography;

type Row = {
  key: string;
  doc: Record<string, unknown> | null;
  req: RouteViewModelDocumentRequirement | null;
  definition: BusinessDocumentDefinition;
  active: boolean;
};

export interface DocumentsBlockProps {
  model: RouteViewModelDocuments;
  route: Route | null;
  onUploadSuccess?: () => void;
}

const DocumentsBlock: React.FC<DocumentsBlockProps> = ({ model, route, onUploadSuccess }) => {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadPreset, setUploadPreset] = useState<{
    documentType: string;
    stage: 'loading' | 'unloading' | 'other';
    fileType: 'file' | 'photo';
  } | null>(null);
  const mergedRaw = useMemo(() => {
    const a = (model.raw.documents || []) as Record<string, unknown>[];
    const b = (model.raw.loadingDocuments || []) as Record<string, unknown>[];
    const c = (model.raw.transportDocuments || []) as Record<string, unknown>[];
    return [...a, ...b, ...c];
  }, [model.raw.documents, model.raw.loadingDocuments, model.raw.transportDocuments]);

  const rows: Row[] = useMemo(
    () =>
      listBusinessDocumentDefinitions({
        party: 'CARRIER',
        mode: normalizeBusinessTransportMode(route?.transportType),
      }).map((definition) => {
        const req = model.requirements.find((item) => item.key === definition.key) ?? null;
        const doc =
          mergedRaw.find((d) => routeDocMatchesRequirement(d, definition.key)) ?? null;
        return {
          key: definition.key,
          doc,
          req,
          definition,
          active: isBusinessDocumentDefinitionActive(definition, { route }),
        };
      }),
    [model.requirements, mergedRaw, route]
  );

  const statusDisplay = (row: Row): { label: string; hint?: string | null; color: string } => {
    const hasRouteContractMeta =
      row.definition.key === 'CARRIER_CONTRACT_REQUEST' &&
      Boolean(route?.carrierContractRequestNumber || route?.carrierContractRequestDate || route?.carrierContractRequestFile);
    const hasDoc = (row.doc != null && row.doc.id != null) || hasRouteContractMeta;
    if (hasDoc) {
      return { label: 'загружен', color: '#52c41a', hint: row.req?.hint };
    }
    if (!row.active) {
      return { label: 'недоступен', color: '#8c8c8c', hint: 'Этап еще не наступил' };
    }
    if (row.req?.status === 'OVERDUE') {
      return { label: 'просрочен', color: '#ff4d4f', hint: row.req.hint };
    }
    return { label: 'доступен', color: '#1677ff', hint: row.req?.hint };
  };

  const hintText = (h: string | null | undefined) => (h != null && String(h).trim() ? String(h) : '—');

  return (
    <Card size="small" title={<Space><FileTextOutlined />Документы</Space>} style={{ marginBottom: 16 }}>
      <UploadDocumentModal
        visible={uploadOpen}
        preset={uploadPreset ?? undefined}
        route={route ?? undefined}
        onCancel={() => {
          setUploadOpen(false);
          setUploadPreset(null);
        }}
        onSuccess={() => {
          onUploadSuccess?.();
        }}
      />
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.7 }}>
        {rows.map((row) => {
          const st = statusDisplay(row);
          const hasDoc = row.doc != null && row.doc.id != null;
          const preset = uploadPresetForRouteRequirement(row.key);
          const showUpload =
            Boolean(route?.id) && !hasDoc && row.active && preset != null && (st.label === 'доступен' || st.label === 'просрочен');
          return (
            <li key={row.key} style={{ marginBottom: 6 }}>
              <Text strong>{row.definition.label || documentTypeLabel(row.key)}</Text>
              <Text style={{ color: st.color, marginLeft: 8 }}>{st.label}</Text>
              {showUpload ? (
                <Button
                  type="link"
                  size="small"
                  icon={<UploadOutlined />}
                  style={{ padding: '0 0 0 8px', height: 'auto' }}
                  onClick={() => {
                    setUploadPreset(preset);
                    setUploadOpen(true);
                  }}
                >
                  Загрузить
                </Button>
              ) : null}
              <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
                {hintText(st.hint)}
              </Text>
            </li>
          );
        })}
      </ul>
    </Card>
  );
};

export default DocumentsBlock;
